import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { __rest } from 'tslib';
import { jsx } from 'react/jsx-runtime';
import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import { useGridApiRef, DataGridPremium } from '@mui/x-data-grid-premium';
import classnames from 'classnames';
import { Checkbox } from '@cognite/cogs-core';
import { FilterIcon, ReorderAscendingIcon, ReorderDescendingIcon } from '@cognite/cogs-icons';
import { COGS_GENERATED_SKELETON_ROW_KEY } from './constants.js';
import { DataGridPagination } from './data-grid-pagination.js';
import { DataGridContext } from './datagrid-context.js';
import { DatagridToolbarColumnHeaderFilter } from './datagrid-filters.js';
import { DataGridHeaderCell, DataGridColumnFilteredIcon, DataGridColumnReorderIcon, DataGridColumnSortedAscendingIcon, DataGridColumnSortedDescendingIcon, DataGridNoRows, DataGridRow, DataGridColumnMenu, DataGridNoResult, DataGridToolbar } from './datagrid-slots.js';
import { convertToDatagridSortType, MAP_SIZE_NAMES_TO_HEIGHT } from './utils.js';

/**
 * DataGrid
 * =============================================================================
 * A fast and extendable table component based on MUI Data Grid
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-datagrid--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <DataGrid data={myData} columns={myColumns} />
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param data - The data to show in the datagrid.
 * @param columns - The column names and custom renderers.
 * @param onRowClick - Optional callback function when a row is clicked.
 * @param onSortClick - Optional function that allows to use server-side sorting.
 * @param selectedRows - Optional  Sets selected rows for the grid.
 * @param onRowSelectionChange - Optional callback that is fired when the selection state of one or multiple rows changes.
 * @param enableCheckboxSelection - Optional. If true, the first column will be a column with checkboxes that allow to select rows.
 * @param disableSelectAllCheckbox - Optional. If true, removes checkbox from column headers that selects or de-selects all table rows. Note: this prop only has effect if enableCheckboxSelection is true.

 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-datagrid--base)
 */
var slotProps = {
  baseIconButton: {
    disableRipple: true
  }
};
var DataGrid = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var _b;
  var data = _a.data,
    columns = _a.columns,
    onRowClick = _a.onRowClick,
    _a$pagination = _a.pagination,
    pagination = _a$pagination === undefined ? true : _a$pagination,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    loading = _a.loading,
    _a$loadingRows = _a.loadingRows,
    loadingRows = _a$loadingRows === undefined ? 4 : _a$loadingRows,
    onSortClick = _a.onSortClick,
    onRowsScrollEnd = _a.onRowsScrollEnd,
    treeDataKey = _a.treeDataKey,
    selectedRows = _a.selectedRows,
    isRowSelectable = _a.isRowSelectable,
    onRowSelectionChange = _a.onRowSelectionChange,
    enableCheckboxSelection = _a.checkboxSelection,
    disableColumnReorder = _a.disableColumnReorder,
    disableSelectAllCheckbox = _a.disableSelectAllCheckbox,
    disableRowSelectionOnRowClick = _a.disableRowSelectionOnRowClick,
    disableRowStripes = _a.disableRowStripes,
    onFilterModelChange = _a.onFilterModelChange,
    columnVisibilityModel = _a.columnVisibilityModel,
    popoverPortalTarget = _a.popoverPortalTarget,
    filterModel = _a.filterModel,
    localeText = _a.localeText,
    _a$alignPagination = _a.alignPagination,
    alignPagination = _a$alignPagination === undefined ? 'right' : _a$alignPagination,
    userProvidedSlots = _a.slots,
    className = _a.className,
    groupingColDef = _a.groupingColDef,
    onRowExpansionChange = _a.onRowExpansionChange,
    _a$enableToolbar = _a.enableToolbar,
    enableToolbar = _a$enableToolbar === undefined ? false : _a$enableToolbar,
    userSlotProps = _a.slotProps,
    userApiRef = _a.apiRef,
    isGroupExpandedByDefault = _a.isGroupExpandedByDefault,
    pageSizeOptions = _a.pageSizeOptions,
    initialState = _a.initialState,
    rowCount = _a.rowCount,
    rest = __rest(_a, ["data", "columns", "onRowClick", "pagination", "size", "loading", "loadingRows", "onSortClick", "onRowsScrollEnd", "treeDataKey", "selectedRows", "isRowSelectable", "onRowSelectionChange", "checkboxSelection", "disableColumnReorder", "disableSelectAllCheckbox", "disableRowSelectionOnRowClick", "disableRowStripes", "onFilterModelChange", "columnVisibilityModel", "popoverPortalTarget", "filterModel", "localeText", "alignPagination", "slots", "className", "groupingColDef", "onRowExpansionChange", "enableToolbar", "slotProps", "apiRef", "isGroupExpandedByDefault", "pageSizeOptions", "initialState", "rowCount"]);
  var internalApiRef = useGridApiRef();
  var apiRef = userApiRef || internalApiRef;
  var columnWidthState = useRef({});
  var customColumns = useMemo(function () {
    return columns === null || columns === undefined ? undefined : columns.map(function (column) {
      var _customWidth = columnWidthState.current[column.field] || column.width;
      var _widthConfig = _customWidth ? {
        width: columnWidthState.current[column.field] || column.width
      } : {};
      return Object.assign(Object.assign({
        cellClassName: classnames({
          'cogs-clickable': Boolean(column.onCellClick)
        }),
        sortable: column.sortable === true,
        renderHeader: function renderHeader(params) {
          return jsx(DataGridHeaderCell, {
            colDef: params.colDef,
            filterModel: filterModel,
            onFilterModelChange: onFilterModelChange,
            portalTarget: popoverPortalTarget,
            localeText: localeText
          });
        }
      }, column), _widthConfig);
    });
  }, [columns, filterModel, localeText, onFilterModelChange, popoverPortalTarget]);
  var slots = useMemo(function () {
    return Object.assign(Object.assign(Object.assign({
      columnFilteredIcon: DataGridColumnFilteredIcon,
      columnUnsortedIcon: DataGridColumnReorderIcon,
      columnSortedAscendingIcon: DataGridColumnSortedAscendingIcon,
      columnSortedDescendingIcon: DataGridColumnSortedDescendingIcon,
      noRowsOverlay: DataGridNoRows,
      pagination: DataGridPagination,
      row: DataGridRow,
      loadingOverlay: function loadingOverlay() {
        return null;
      },
      columnMenu: DataGridColumnMenu,
      columnMenuFilterIcon: FilterIcon,
      columnMenuUnsortedIcon: DataGridColumnReorderIcon,
      columnMenuSortAscendingIcon: ReorderAscendingIcon,
      columnMenuSortDescendingIcon: ReorderDescendingIcon,
      noResultsOverlay: DataGridNoResult,
      baseCheckbox: Checkbox
    }, enableToolbar ? {
      toolbar: DataGridToolbar
    } : {}), {
      filterPanel: DatagridToolbarColumnHeaderFilter
    }), userProvidedSlots);
  }, [enableToolbar, userProvidedSlots]);
  var contextValue = useMemo(function () {
    return {
      localeText: localeText
    };
  }, [localeText]);
  var containerClassName = useMemo(function () {
    return classnames('cogs-lab', 'cogs-datagrid', className, {
      onRowClick: Boolean(onRowClick),
      // Used to set the cursor to pointer for the rows if a callback is provided
      hideSelectAllCheckbox: Boolean(disableSelectAllCheckbox)
    });
  }, [onRowClick, disableSelectAllCheckbox, className]);
  var onCellClick = useCallback(function (params) {
    var _a, _b;
    (_b = (_a = columns === null || columns === undefined ? undefined : columns.find(function (col) {
      return (col === null || col === undefined ? undefined : col.field) === params.field;
    })) === null || _a === undefined ? undefined : _a.onCellClick) === null || _b === undefined ? undefined : _b.call(_a, params);
  }, [columns]);
  /**
   * generating skeleton rows based on config given
   */
  var keyArr = Array(loadingRows).keys();
  var skeletonRows = Array.from(keyArr).map(function (key) {
    var skeletonRowId = "cogs-automatic-generated-skeleton-".concat(key);
    return Object.assign(Object.assign({
      id: skeletonRowId
    }, treeDataKey ? _defineProperty({}, treeDataKey, [skeletonRowId]) : {}), _defineProperty({}, COGS_GENERATED_SKELETON_ROW_KEY, true));
  });
  var rows = useMemo(function () {
    return loading ? data.concat(skeletonRows) : data;
  }, [loading, data, skeletonRows]);
  /**
   * using this way of calculating rowCount since we are passing it in a custom way
   */
  var calculatedRowCount = React.useMemo(function () {
    return rowCount !== null && rowCount !== undefined ? rowCount : data.length;
  }, [rowCount, data]);
  var getTreeDataPath = useMemo(function () {
    if (!treeDataKey) return undefined;
    return function (row) {
      return row[treeDataKey];
    };
  }, [treeDataKey]);
  var handleSortClick = useCallback(function (model) {
    var nextSortParams = convertToDatagridSortType(model);
    if (onSortClick) onSortClick(nextSortParams);
  }, [onSortClick]);
  useEffect(function () {
    // Maintain the expansion state of the rows
    var rowExpansionEvent = apiRef.current.subscribeEvent('rowExpansionChange', function (node) {
      var row = apiRef.current.getRow(node.id);
      onRowExpansionChange === null || onRowExpansionChange === undefined ? undefined : onRowExpansionChange(node, row);
    });
    var columnWidthChangeEvent = apiRef.current.subscribeEvent('columnWidthChange', function (col) {
      columnWidthState.current[col.colDef.field] = col.width;
    });
    return function () {
      rowExpansionEvent();
      columnWidthChangeEvent();
    };
  }, []);
  /**
   * this initial state is required to initialize pageSize of datagrid
   * and pagination component initial state
   */
  var paginationInitialState = {
    pagination: Object.assign(Object.assign({}, initialState === null || initialState === undefined ? undefined : initialState.pagination), {
      paginationModel: Object.assign({
        pageSize: pageSizeOptions ? pageSizeOptions[0] : 10
      }, (_b = initialState === null || initialState === undefined ? undefined : initialState.pagination) === null || _b === undefined ? undefined : _b.paginationModel)
    })
  };
  return jsx(DataGridContext.Provider, {
    value: contextValue,
    children: jsx(DataGridPremium, Object.assign({
      id: "cogs-datagrid",
      getRowClassName: function getRowClassName() {
        return classnames("cogs-datagrid__row--".concat(size), {
          'cogs-datagrid__row--striped': !disableRowStripes,
          'cogs-datagrid__row--multiselect': enableCheckboxSelection
        });
      },
      groupingColDef: Object.assign({
        width: columnWidthState.current['__tree_data_group__'] || (groupingColDef === null || groupingColDef === undefined ? undefined : groupingColDef.width) || 220,
        renderHeader: function renderHeader(params) {
          return jsx(DataGridHeaderCell, {
            colDef: params.colDef,
            filterModel: filterModel,
            onFilterModelChange: onFilterModelChange,
            portalTarget: popoverPortalTarget,
            localeText: localeText
          });
        }
      }, groupingColDef),
      loading: loading,
      treeData: Boolean(treeDataKey),
      getTreeDataPath: getTreeDataPath,
      ref: ref,
      apiRef: apiRef,
      rowHeight: MAP_SIZE_NAMES_TO_HEIGHT[size],
      columnHeaderHeight: MAP_SIZE_NAMES_TO_HEIGHT[size],
      rows: rows,
      onRowClick: onRowClick,
      onCellClick: onCellClick,
      onRowsScrollEnd: onRowsScrollEnd,
      columns: customColumns,
      className: containerClassName,
      sortingMode: onSortClick ? 'server' : 'client',
      onSortModelChange: onSortClick ? handleSortClick : undefined,
      slots: slots,
      onFetchRows: onRowsScrollEnd,
      slotProps: Object.assign(Object.assign({
        pagination: {
          pageSizeOptions: pageSizeOptions,
          itemsPerPage: paginationInitialState.pagination.paginationModel.pageSize,
          rowCount: calculatedRowCount
        },
        footer: {
          className: "cogs-datagrid-footer--align-pagination-".concat(alignPagination)
        }
      }, slotProps), userSlotProps),
      disableColumnSelector: true,
      hideFooter: !pagination,
      rowSelectionModel: selectedRows,
      isRowSelectable: isRowSelectable,
      onRowSelectionModelChange: onRowSelectionChange,
      checkboxSelection: enableCheckboxSelection,
      disableRowSelectionOnClick: disableRowSelectionOnRowClick,
      columnVisibilityModel: columnVisibilityModel,
      disableColumnReorder: disableColumnReorder,
      isGroupExpandedByDefault: isGroupExpandedByDefault,
      pagination: pagination,
      disableColumnMenu: true,
      pageSizeOptions: pageSizeOptions,
      initialState: Object.assign(Object.assign({}, initialState), paginationInitialState),
      rowCount: calculatedRowCount
    }, rest))
  });
});

export { DataGrid };
